/**
 * WalkPreview — first-person "walk it yourself" mode in the editor, used to
 * pick the viewer's starting position by actually standing where you want the
 * visitor to spawn.
 *
 * While active it TAKES OVER the editor camera (Camera.walkPreview = true makes
 * the normal orbit/fly update bail) and drives it with the SAME walk physics
 * the published viewer uses: each frame the position resolves against the full
 * surface list (ground + extra levels + stairs) via the shared walk-geometry
 * module (buildWalkSurfaces / resolveWalkPoint / seedWalkPoint), so the floor
 * heights, stair ramps and collision bounds the author tests here are byte-for-
 * byte what the viewer enforces — change any of them and this moves with it,
 * by construction.
 *
 * Controls: WASD / arrows to walk, drag (left mouse) to look, Esc to exit.
 * "Set start view from here" reuses the existing camera.setStartPose path,
 * which captures the live camera position + forward — i.e. wherever you're
 * standing and looking right now.
 *
 * With collision voxels loaded (walk-voxels.ts) the shared voxel-walk code
 * (scene-core/voxel-walk.ts) adds collision: the surfaces still give XZ and
 * the height, the voxels stop the body at objects and walls.
 *
 * The walk FEEL (no easing/inertia, no hover-look, no click-to-move) is
 * deliberately simpler than the viewer's WalkController — this is an authoring
 * aid for placing the spawn, not a fidelity-critical playthrough. The parts
 * that MUST match the viewer (height + collision) are shared; the rest isn't.
 */

import { Quat, Vec3 } from 'playcanvas';

import type { Events } from '../events';
import type { Scene } from '../scene';
import type { WalkArea } from './walk-area';
import {
    buildWalkSurfaces, resolveWalkPoint, seedWalkPoint,
    CANONICAL_EYE_HEIGHT, type WalkSurface
} from './walk-geometry';
import {
    resolveVoxelWalkPoint, seedVoxelWalkPoint,
    type VoxelCollider, type VoxelWalkPoint
} from '../../../shared/scene-core/voxel-walk';

const WALK_SPEED = 2.0;                       // units/sec (matches viewer)
const PITCH_CLAMP = (60 * Math.PI) / 180;     // ±60° (matches viewer)
const LOOK_SENS = 0.0026;                      // radians of look per pixel dragged
const RAD2DEG = 180 / Math.PI;
const WALK_KEYS = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];

const walkFwd = new Vec3();
const walkRight = new Vec3();

class WalkPreview {
    // while walking, the camera follows this instead of its orbit/fly tweens
    private override: { position: Vec3; rotation: Quat; fov: number; near: number; far: number } | null = null;
    private readonly events: Events;
    private readonly scene: Scene;

    private active = false;

    // physics state — the SAME multi-level surface list the viewer builds.
    private surfaces: WalkSurface[] = [];
    private eyeHeight = CANONICAL_EYE_HEIGHT;
    private posX = 0;
    private posZ = 0;
    private feetY = NaN;   // resolved floor height under the camera
    private voxels: VoxelCollider | null = null;
    private voxelPoint: VoxelWalkPoint | null = null;   // standing point on the voxels
    private currentY = NaN;
    private yaw = 0;     // radians
    private pitch = 0;   // radians

    // input
    private readonly keys = new Set<string>();
    private dragging = false;
    private lastPX = 0;
    private lastPY = 0;
    private cleanupFns: Array<() => void> = [];

    // saved editor-camera pose, restored on exit
    private saved: { focal: Vec3; azim: number; elev: number; distance: number; fov: number } | null = null;

    constructor(events: Events, scene: Scene) {
        this.events = events;
        this.scene = scene;

        events.function('walk.preview.active', () => this.active);
        // where the preview stands (for checks from the console / automation)
        events.function('walk.preview.state', () => (this.active
            ? { x: this.posX, z: this.posZ, feetY: this.feetY, eyeY: this.currentY, voxels: !!this.voxels }
            : null));
        events.on('walk.preview.toggle', () => this.toggle());
        events.on('walk.preview.enter', () => this.enter());
        events.on('walk.preview.exit', () => this.exit());

        // Drive the camera after the camera element's (now-skipped) onUpdate.
        events.on('update', (dt: number) => this.onUpdate(dt));

        // Bail out if the scene is torn down / replaced while walking.
        events.on('scene.clear', () => { if (this.active) this.exit(); });

        // Track eye-height edits LIVE. enter() snapshots the walk area, so
        // without this the height you type while standing in the preview does
        // nothing until you exit and re-enter — it reads as "the number has no
        // effect". Re-reading it here makes the camera rise/drop as you type,
        // which is the only way to judge the value against the space.
        events.on('walk.changed', () => {
            if (!this.active) return;
            const wa = this.walkArea();
            if (!wa) return;
            const h = wa.eyeHeight > 0 ? wa.eyeHeight : CANONICAL_EYE_HEIGHT;
            if (h === this.eyeHeight) return;
            this.eyeHeight = h;
            this.scene.forceRender = true;
        });
    }

    private walkArea(): WalkArea | undefined {
        return (this.scene as any).walkArea as WalkArea | undefined;
    }

    /** Walk preview needs a finished walk area (floor + ≥3 boundary points). */
    canEnter(): boolean {
        const wa = this.walkArea();
        return !!(wa && wa.hasFloor && wa.boundary.length >= 3);
    }

    toggle() {
        if (this.active) this.exit();
        else this.enter();
    }

    enter() {
        if (this.active) return;
        const wa = this.walkArea();
        if (!wa || !this.canEnter()) {
            this.events.fire('walk.status', 'Set a floor and at least 3 boundary points before walking.');
            return;
        }

        // Snapshot the walkable surfaces (ground + levels + stairs) exactly as
        // the viewer will build them from the exported walk block.
        const cfg = wa.serialize();
        if (!cfg) {
            this.events.fire('walk.status', 'Set a floor and at least 3 boundary points before walking.');
            return;
        }
        this.surfaces = buildWalkSurfaces(cfg);
        this.voxels = (this.events.invoke('walk.voxels.collider') as VoxelCollider | null) ?? null;
        this.eyeHeight = wa.eyeHeight > 0 ? wa.eyeHeight : CANONICAL_EYE_HEIGHT;

        // Save the editor camera so we can drop the author back exactly where
        // they were when they exit the preview.
        const cam = this.scene.camera;
        this.saved = {
            focal: cam.focalPoint.clone(),
            azim: cam.azim,
            elev: cam.elevation,
            distance: cam.distance,
            fov: cam.fov
        };

        // Seed the spawn: stand at the stored start pose if one exists (so the
        // author refines it in place), otherwise at the ground-boundary
        // centroid. The start pose's HEIGHT picks which floor to spawn on.
        const start = this.events.invoke('camera.getStartPose') as
            { position: number[]; target: number[] } | null;
        let preferFeetY = NaN;
        if (start) {
            this.posX = start.position[0];
            this.posZ = start.position[2];
            preferFeetY = start.position[1] - this.eyeHeight;
            const dx = start.target[0] - start.position[0];
            const dy = start.target[1] - start.position[1];
            const dz = start.target[2] - start.position[2];
            const l = Math.hypot(dx, dy, dz) || 1;
            this.yaw = Math.atan2(-dx, -dz);
            this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, Math.asin(dy / l)));
        } else {
            const ground = this.surfaces[0]?.poly ?? [];
            let cx = 0, cz = 0;
            for (const b of ground) { cx += b[0]; cz += b[1]; }
            const n = ground.length || 1;
            this.posX = cx / n;
            this.posZ = cz / n;
            this.yaw = cam.azim * Math.PI / 180;
            this.pitch = 0;
        }
        if (this.voxels) {
            const seed = seedVoxelWalkPoint(this.voxels, this.surfaces, this.posX, this.posZ, preferFeetY);
            this.voxelPoint = seed;
            this.posX = seed.x;
            this.posZ = seed.z;
            this.feetY = seed.y;
        } else {
            const seed = seedWalkPoint(this.surfaces, this.posX, this.posZ, preferFeetY);
            this.voxelPoint = null;
            this.posX = seed.x;
            this.posZ = seed.z;
            this.feetY = seed.y;
        }
        this.currentY = NaN;

        // Drop any active authoring tool so its click handlers don't fight
        // the walk input.
        this.events.fire('tool.deactivate');

        this.attach();
        this.override = { position: new Vec3(), rotation: new Quat(), fov: cam.fov, near: cam.near, far: cam.far };
        cam.setPoseOverride(this.override);
        this.active = true;
        this.scene.forceRender = true;
        this.events.fire('walk.preview.changed', true);
        this.events.fire('walk.status', 'Walking — WASD to move, drag to look, Esc to exit. Then "Set start view from here".');
    }

    exit() {
        if (!this.active) return;
        this.active = false;
        this.detach();

        const cam = this.scene.camera;
        cam.setPoseOverride(null);
        this.override = null;
        // Restore the editor camera exactly (no animation).
        if (this.saved) {
            cam.setFocalPoint(this.saved.focal, 0);
            cam.setAzimElev(this.saved.azim, this.saved.elev, 0);
            cam.setDistance(this.saved.distance, 0);
            cam.fov = this.saved.fov;
            this.saved = null;
        }
        this.scene.forceRender = true;
        this.events.fire('walk.preview.changed', false);
        this.events.fire('walk.status', '');
    }

    // ── per-frame drive ──────────────────────────────────────────────────
    private onUpdate(dt: number) {
        if (!this.active) return;
        const cam = this.scene.camera;
        const ov = this.override;
        if (!ov) return;

        // Orientation first, so forward/right reflect this frame's view.
        ov.rotation.setFromEulerAngles(this.pitch * RAD2DEG, this.yaw * RAD2DEG, 0);
        const forward = ov.rotation.transformVector(Vec3.FORWARD, walkFwd);

        // WASD / arrows → horizontal movement along the camera basis.
        let tf = 0, tr = 0;
        if (this.keys.has('w') || this.keys.has('arrowup')) tf += 1;
        if (this.keys.has('s') || this.keys.has('arrowdown')) tf -= 1;
        if (this.keys.has('a') || this.keys.has('arrowleft')) tr -= 1;
        if (this.keys.has('d') || this.keys.has('arrowright')) tr += 1;

        let nextX = this.posX;
        let nextZ = this.posZ;
        if (tf !== 0 || tr !== 0) {
            const m = Math.hypot(tf, tr) || 1;
            tf /= m; tr /= m;
            const fwd = forward;
            const right = ov.rotation.transformVector(Vec3.RIGHT, walkRight);
            let fx = fwd.x, fz = fwd.z;
            const fl = Math.hypot(fx, fz) || 1; fx /= fl; fz /= fl;
            let rx = right.x, rz = right.z;
            const rl = Math.hypot(rx, rz) || 1; rx /= rl; rz /= rl;
            nextX += (fx * tf + rx * tr) * WALK_SPEED * dt;
            nextZ += (fz * tf + rz * tr) * WALK_SPEED * dt;
        }

        // Resolve against the walkable surfaces (levels + stairs): XZ clamps to
        // whatever is reachable from the current floor height, eye = floor
        // height + eyeHeight. SHARED with the viewer — identical by construction.
        // With voxels, the same surfaces still set XZ and the height; the
        // voxels only block the body (shared voxel-walk, as in the viewer).
        let r: { x: number; z: number; y: number };
        if (this.voxels && this.voxelPoint) {
            this.voxelPoint = resolveVoxelWalkPoint(this.voxels, this.surfaces, this.eyeHeight, this.voxelPoint, nextX, nextZ);
            r = this.voxelPoint;
        } else {
            r = resolveWalkPoint(this.surfaces, this.feetY, nextX, nextZ);
        }
        this.posX = r.x; this.posZ = r.z;
        this.feetY = r.y;
        this.currentY = r.y + this.eyeHeight;

        ov.position.set(r.x, this.currentY, r.z);
        cam.fitClippingPlanes(ov.position, forward);
        ov.near = cam.near;
        ov.far = cam.far;
        cam.setPoseOverride(ov);
        this.scene.forceRender = true;
    }

    // ── input ────────────────────────────────────────────────────────────
    private attach() {
        const target = document.getElementById('canvas-container');
        if (!target) return;

        const onDown = (e: PointerEvent) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            this.dragging = true;
            this.lastPX = e.clientX; this.lastPY = e.clientY;
            target.setPointerCapture?.(e.pointerId);
        };
        const onMove = (e: PointerEvent) => {
            if (!this.dragging) return;
            const dx = e.clientX - this.lastPX;
            const dy = e.clientY - this.lastPY;
            this.lastPX = e.clientX; this.lastPY = e.clientY;
            this.yaw -= dx * LOOK_SENS;
            this.pitch -= dy * LOOK_SENS;
            if (this.pitch > PITCH_CLAMP) this.pitch = PITCH_CLAMP;
            else if (this.pitch < -PITCH_CLAMP) this.pitch = -PITCH_CLAMP;
            this.scene.forceRender = true;
        };
        const onUp = (e: PointerEvent) => {
            this.dragging = false;
            target.releasePointerCapture?.(e.pointerId);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            const k = e.key.toLowerCase();
            if (k === 'escape') { e.stopPropagation(); this.exit(); return; }
            if (WALK_KEYS.includes(k)) {
                this.keys.add(k);
                e.preventDefault();
                // keep the editor's own shortcuts (W/A/S/D tools) out of it
                e.stopPropagation();
            }
        };
        const onKeyUp = (e: KeyboardEvent) => {
            const k = e.key.toLowerCase();
            this.keys.delete(k);
            if (WALK_KEYS.includes(k)) e.stopPropagation();
        };
        const onBlur = () => this.keys.clear();

        // Capture phase + stopPropagation so the orbit controller (same target)
        // doesn't also react to our look-drag.
        const opts = { capture: true } as AddEventListenerOptions;
        const downWrap = (e: PointerEvent) => { onDown(e); e.stopPropagation(); };
        const moveWrap = (e: PointerEvent) => { onMove(e); if (this.dragging) e.stopPropagation(); };
        const upWrap = (e: PointerEvent) => { onUp(e); };

        target.addEventListener('pointerdown', downWrap as EventListener, opts);
        target.addEventListener('pointermove', moveWrap as EventListener, opts);
        target.addEventListener('pointerup', upWrap as EventListener, opts);
        // Keys on window in the capture phase: the first listener on the page,
        // so editor handlers that stop key events can't starve the walk.
        window.addEventListener('keydown', onKeyDown, opts);
        window.addEventListener('keyup', onKeyUp, opts);
        window.addEventListener('blur', onBlur);

        this.cleanupFns.push(
            () => target.removeEventListener('pointerdown', downWrap as EventListener, opts),
            () => target.removeEventListener('pointermove', moveWrap as EventListener, opts),
            () => target.removeEventListener('pointerup', upWrap as EventListener, opts),
            () => window.removeEventListener('keydown', onKeyDown, opts),
            () => window.removeEventListener('keyup', onKeyUp, opts),
            () => window.removeEventListener('blur', onBlur)
        );
    }

    private detach() {
        for (const fn of this.cleanupFns) fn();
        this.cleanupFns = [];
        this.keys.clear();
        this.dragging = false;
    }
}

export { WalkPreview };
